/**
 * Area queries over the hex grid — ranges, rings, spirals — built purely from
 * the {@link Hex} algebra in `coord.ts`. No movement cost or obstacles here;
 * that's pathfinding (`path.ts`). These answer "which hexes are within / at a
 * given radius" — the geometry behind blast templates, threat/aura overlays,
 * and naive movement reach.
 */

import { type Hex, add, axial, direction, distance, hexKey, neighbor, scale } from "./coord";

/**
 * All hexes within `n` steps of `center` (inclusive), `n >= 0`. Size is the
 * centered hexagonal number `3n(n+1) + 1`. Returned in a stable row-major-ish
 * order (by `q`, then `r`).
 */
export function range(center: Hex, n: number): Hex[] {
  const out: Hex[] = [];
  for (let q = -n; q <= n; q++) {
    const rLo = Math.max(-n, -q - n);
    const rHi = Math.min(n, -q + n);
    for (let r = rLo; r <= rHi; r++) {
      out.push(add(center, axial(q, r)));
    }
  }
  return out;
}

/**
 * The ring of hexes exactly `radius` steps from `center`. Empty when
 * `radius < 0`; `[center]` when `radius === 0`. Walks the ring starting from
 * the hex `radius` steps "south-west" (direction 4), in clockwise order.
 */
export function ring(center: Hex, radius: number): Hex[] {
  if (radius < 0) return [];
  if (radius === 0) return [center];
  const out: Hex[] = [];
  let h = add(center, scale(direction(4), radius));
  for (let i = 0; i < 6; i++) {
    for (let step = 0; step < radius; step++) {
      out.push(h);
      h = neighbor(h, i);
    }
  }
  return out;
}

/**
 * `center` followed by each ring outward up to `radius` — the spiral traversal.
 * Useful when you want hexes ordered by increasing distance (e.g. nearest-first
 * search seeds).
 */
export function spiral(center: Hex, radius: number): Hex[] {
  const out: Hex[] = [center];
  for (let k = 1; k <= radius; k++) {
    out.push(...ring(center, k));
  }
  return out;
}

/** Whether `h` is within `n` steps of `center` (inclusive). */
export function within(center: Hex, h: Hex, n: number): boolean {
  return distance(center, h) <= n;
}

/**
 * The two adjacent directions a **tick** step grows toward ("top, top-right"),
 * and the opposite pair a **tock** step grows toward ("bottom, bottom-left").
 * Under the `FLAT` layout (screen `-y` = up) these index {@link DIRECTIONS} as
 * top-right=1, top=2 and their 180° opposites bottom-left=4, bottom=5. The pair
 * choice is purely cosmetic orientation — *any* adjacent pair + its opposite
 * yields the same hex counts and the same odd-size disks; this one just makes
 * the growth read as up-and-right on screen.
 */
const TICK_DIRS = [1, 2] as const;
const TOCK_DIRS = [4, 5] as const;

/** Add every `dirs`-neighbour of `cells` to `cells` (dedup by key). */
function dilate(cells: Hex[], dirs: readonly number[]): Hex[] {
  const seen = new Map<string, Hex>(cells.map((c) => [hexKey(c), c]));
  for (const c of cells) {
    for (const d of dirs) {
      const n = neighbor(c, d);
      seen.set(hexKey(n), n);
    }
  }
  return [...seen.values()];
}

/**
 * The hexes a unit of Lancer **Size** `size` occupies, anchored at `anchor`.
 *
 * Lancer states Size in *square* grid spaces ("N on each side"), which has no
 * N×N analogue on hexes, and a concentric-disk reading (1, 7, 19, …) makes
 * mechs absurdly large. Instead we grow the footprint by a **tick-tock**
 * directional dilation: start with the anchor hex, then each size step adds the
 * neighbours in two adjacent directions, alternating between {@link TICK_DIRS}
 * (even steps) and the opposite {@link TOCK_DIRS} (odd steps). Counts come out
 * 1, 3, 7, 12, 19, 27, 37 (= `3k(k+1)+1` for odd `size=2k+1`, `3m²` for even
 * `size=2m`).
 *
 * Because tock-directions are tick rotated 180°, **odd sizes are exact
 * concentric disks** (`size 1` → radius 0, `size 3` → radius 1, `size 5` →
 * radius 2 = the classic "hex with two rings", …) while even sizes are the
 * half-grown shapes between them. The **anchor stays a hex** — the shape grows
 * up-and-right, so a placed token needs only `(anchor, size)`, no vertex
 * bookkeeping. `ceil` collapses Size ½ to a single hex. Pure integer cube math.
 */
export function footprint(anchor: Hex, size: number): Hex[] {
  const steps = Math.max(1, Math.ceil(size));
  let cells: Hex[] = [anchor];
  for (let step = 2; step <= steps; step++) {
    cells = dilate(cells, step % 2 === 0 ? TICK_DIRS : TOCK_DIRS);
  }
  return cells;
}
